import { useState, useEffect, createContext, useContext, ReactNode } from 'react';

// 认证配置
const AUTH_CONFIG = {
  username: process.env.REACT_APP_AUTH_USERNAME || 'admin',
  password: process.env.REACT_APP_AUTH_PASSWORD || 'admin123',
};

interface AuthState {
  isAuthenticated: boolean;
  user: string | null;
}

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string; remainingAttempts?: number }>;
  logout: () => void;
  getFailedAttempts: () => number;
  isLocked: () => boolean;
  getLockRemainingTime: () => number;
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_STORAGE_KEY = 'bankcard_auth';
const AUTH_EXPIRY_KEY = 'bankcard_auth_expiry';
const FAILED_ATTEMPTS_KEY = 'bankcard_failed_attempts';
const LOCK_UNTIL_KEY = 'bankcard_lock_until';
const LAST_ACTIVITY_KEY = 'bankcard_last_activity';

const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24小时
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30分钟无操作自动登出
const MAX_FAILED_ATTEMPTS = 10;
const LOCK_DURATION = 30 * 60 * 1000; // 锁定30分钟

function getStoredAuth(): AuthState {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    const expiry = localStorage.getItem(AUTH_EXPIRY_KEY);
    
    if (stored && expiry) {
      const expiryTime = parseInt(expiry, 10);
      if (Date.now() < expiryTime) {
        return JSON.parse(stored);
      }
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(AUTH_EXPIRY_KEY);
    }
  } catch {
    // ignore
  }
  return { isAuthenticated: false, user: null };
}

function setStoredAuth(state: AuthState): void {
  if (state.isAuthenticated) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
    localStorage.setItem(AUTH_EXPIRY_KEY, String(Date.now() + SESSION_DURATION));
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(AUTH_EXPIRY_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  }
}

function getFailedAttemptsCount(): number {
  try {
    return parseInt(localStorage.getItem(FAILED_ATTEMPTS_KEY) || '0', 10);
  } catch {
    return 0;
  }
}

function setFailedAttemptsCount(count: number): void {
  localStorage.setItem(FAILED_ATTEMPTS_KEY, String(count));
}

function getLockUntil(): number {
  try {
    return parseInt(localStorage.getItem(LOCK_UNTIL_KEY) || '0', 10);
  } catch {
    return 0;
  }
}

function setLockUntil(time: number): void {
  localStorage.setItem(LOCK_UNTIL_KEY, String(time));
}

function clearLock(): void {
  localStorage.removeItem(FAILED_ATTEMPTS_KEY);
  localStorage.removeItem(LOCK_UNTIL_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>(getStoredAuth);

  // 更新最后活动时间
  const updateLastActivity = () => {
    if (authState.isAuthenticated) {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    }
  };

  useEffect(() => {
    // 监听用户活动
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateLastActivity);
    });

    // 检查会话和空闲超时
    const checkSession = () => {
      const expiry = localStorage.getItem(AUTH_EXPIRY_KEY);
      const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
      
      if (expiry && Date.now() >= parseInt(expiry, 10)) {
        // 会话过期
        setAuthState({ isAuthenticated: false, user: null });
        setStoredAuth({ isAuthenticated: false, user: null });
        return;
      }
      
      if (lastActivity && authState.isAuthenticated) {
        const idleTime = Date.now() - parseInt(lastActivity, 10);
        if (idleTime >= IDLE_TIMEOUT) {
          // 空闲超时
          setAuthState({ isAuthenticated: false, user: null });
          setStoredAuth({ isAuthenticated: false, user: null });
        }
      }
    };

    const interval = setInterval(checkSession, 60000);
    
    return () => {
      clearInterval(interval);
      events.forEach(event => {
        window.removeEventListener(event, updateLastActivity);
      });
    };
  }, [authState.isAuthenticated]);

  const isLocked = (): boolean => {
    const lockUntil = getLockUntil();
    if (lockUntil && Date.now() < lockUntil) {
      return true;
    }
    if (lockUntil && Date.now() >= lockUntil) {
      clearLock();
    }
    return false;
  };

  const getLockRemainingTime = (): number => {
    const lockUntil = getLockUntil();
    if (lockUntil && Date.now() < lockUntil) {
      return Math.ceil((lockUntil - Date.now()) / 1000 / 60);
    }
    return 0;
  };

  const getFailedAttempts = (): number => {
    return getFailedAttemptsCount();
  };

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string; remainingAttempts?: number }> => {
    // 检查是否被锁定
    if (isLocked()) {
      const remaining = getLockRemainingTime();
      return { 
        success: false, 
        error: `Account locked. Try again in ${remaining} minutes.` 
      };
    }

    if (username === AUTH_CONFIG.username && password === AUTH_CONFIG.password) {
      const newState = { isAuthenticated: true, user: username };
      setAuthState(newState);
      setStoredAuth(newState);
      clearLock();
      // 保存凭据用于 API 调用
      localStorage.setItem('bankcard_auth_user', username);
      localStorage.setItem('bankcard_auth_pass', password);
      return { success: true };
    }

    // 登录失败，增加失败次数
    const attempts = getFailedAttemptsCount() + 1;
    setFailedAttemptsCount(attempts);

    if (attempts >= MAX_FAILED_ATTEMPTS) {
      setLockUntil(Date.now() + LOCK_DURATION);
      return { 
        success: false, 
        error: `Too many failed attempts. Account locked for 30 minutes.`,
        remainingAttempts: 0
      };
    }

    return { 
      success: false, 
      error: 'Invalid username or password',
      remainingAttempts: MAX_FAILED_ATTEMPTS - attempts
    };
  };

  const logout = () => {
    const newState = { isAuthenticated: false, user: null };
    setAuthState(newState);
    setStoredAuth(newState);
    // 清除 API 凭据
    localStorage.removeItem('bankcard_auth_user');
    localStorage.removeItem('bankcard_auth_pass');
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, getFailedAttempts, isLocked, getLockRemainingTime }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
